<template lang="pug">
.judgement-template-select-tab(v-cloak)
  .mb-3
  WaiterLabelSettingTable(
    v-model="items"
    :fields="fields"
    :isLoading="isLoading"
  )
  .mb-2(v-if="errorMessage")
    span.error-message {{ errorMessage }}
  .mb-2
  .box-footer.register-footer
    ValidationAlert(v-model="showValidationAlert")
    .buttons.m-0
      button.btn.update-btn(@click="onUpdate" type="button") 更新

  OkCancelModal(v-model="showUpdateModal" title="ラベルを更新しますか？" @ok="update()")
</template>

<script lang="ts">
import { Component, Prop, Inject, Vue } from "vue-property-decorator";

import { userStore } from "@/store/store";

import WaiterLabelsService, { WaiterLabelsGetModel } from "@/commons/services/WaiterLabels";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import WaiterLabelSettingTable, { WaiterLabelSettingTableField, WaiterLabelSettingTableItem } from "@/pc/containers/waiters/WaiterLabelSettingTable.vue";

/**
 * ### 入所評価判定様式タブコンポーネント
 */
@Component({
  components: {
    OkCancelModal,
    ValidationAlert,
    WaiterLabelSettingTable
  },
  $_veeValidate: { validator: "new" }
})
export default class WaiterLabelSettingTab extends Vue {
  /** 待機者ラベルサービス */
  @Inject("WaiterLabelsService") waiterLabelsService!: WaiterLabelsService;

  @Prop()
  errorMessage!: string;

  /** 更新確認モーダル表示可否 */
  showUpdateModal: boolean = false;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** 列名 */
  fields: Array<WaiterLabelSettingTableField> = [];
  /** 行情報 */
  items: Array<WaiterLabelSettingTableItem> = [];
  /** 読込み中か否か */
  isLoading: boolean = true;

  /**
   * ### 画面情報をリフレッシュする
   *
   * - 親コンポーネントから呼び出される
   */
  public async refresh(): Promise<void> {
    await this.createView();
    return;
  }

  /**
   * ### vue lifecycle mounted
   *
   * 画面表示情報の作成を行う
   */
  public async mounted(): Promise<void> {
    // 画面初期表示時に作成して変更しないものを初期化する
    const fields: Array<WaiterLabelSettingTableField> = this.createFields();
    this.fields = fields;

    // 動的に変更する画面情報を作成する
    this.items = [];

    await this.createView();

    return;
  }

  /**
   * ### 列名を作成する
   */
  public createFields(): Array<WaiterLabelSettingTableField> {
    let fields: Array<WaiterLabelSettingTableField> = [];

    fields.push({ key: "use", label: "ラベルの使用", sortable: false, sortDirection: "desc", class: "use" });
    fields.push({ key: "name", label: "ラベル名", sortable: false, sortDirection: "desc", class: "name" });
    fields.push({ key: "description", label: "説明文", sortable: false, sortDirection: "desc", class: "description" });
    fields.push({ key: "labelSelect", label: "ラベル選択", sortable: false, sortDirection: "desc", class: "label-select" });
    fields.push({ key: "preview", label: "プレビュー", sortable: false, sortDirection: "desc", class: "preview" });

    return fields;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    this.isLoading = true;

    const labels: Array<WaiterLabelsGetModel> = await this.getWaiterLabels();
    const items: Array<WaiterLabelSettingTableItem> = this.createItems(labels);
    this.items = items;

    this.isLoading = false;

    return;
  }

  /**
   * ### 待機者ラベルを取得する
   */
  public async getWaiterLabels(): Promise<Array<WaiterLabelsGetModel>> {
    const response: Array<WaiterLabelsGetModel> = await this.waiterLabelsService.gets(userStore);
    return response;
  }

  /**
   * ### 行情報を作成する
   */
  public createItems(labels: Array<WaiterLabelsGetModel>): Array<WaiterLabelSettingTableItem> {
    let items: Array<WaiterLabelSettingTableItem> = [];

    for (const label of labels) {
      let colorId: number = 1;
      switch (label.color) {
        case "#FFFFFF": {
          switch (label.bgColor) {
            case "#0091BF": {
              colorId = 1;
              break;
            }
            case "#00AF3A": {
              colorId = 2;
              break;
            }
            case "#DF5517": {
              colorId = 3;
              break;
            }
            case "#DECB05": {
              colorId = 4;
              break;
            }
            case "#F1396B": {
              colorId = 5;
              break;
            }
          }
          break;
        }
        case "#000000": {
          switch (label.bgColor) {
            case "#70D9F9": {
              colorId = 6;
              break;
            }
            case "#57E78F": {
              colorId = 7;
              break;
            }
            case "#ECAB8F": {
              colorId = 8;
              break;
            }
            case "#F1E583": {
              colorId = 9;
              break;
            }
            case "#F7B4C6": {
              colorId = 10;
              break;
            }
          }
          break;
        }
      }

      items.push({
        ...label,
        colorId: colorId
      });
    }

    return items;
  }

  /**
   * ### 更新ボタン押下イベント
   */
  public async onUpdate(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();
    if (validationResult) {
      this.showValidationAlert = false;
      this.showUpdateModal = true;
    } else {
      this.showValidationAlert = true;
    }
    return;
  }

  /**
   * ### 更新モーダルOKボタン押下イベント
   */
  public update(): void {
    this.$emit("onUpdate", this.items);
    return;
  }
}
</script>

<style lang="scss" scoped>
.judgement-template-select-tab {
  .box-footer {
    padding: 10px 0 0 0 !important;

    .update-btn {
      width: 120px;
    }
  }
}
</style>
